import { createSlice } from '@reduxjs/toolkit';
import type { CertificadoGerado } from '../../types/arbitralDetailTypes';
import type { CertificadoAcessosArbitral } from '../../types/certificadoAcessosTypes';
import { baixarPdfCertificadoAcessos, fetchCertificadoAcessos, fetchCertificados } from './actions';

interface CertificadosArbitraisState {
  items: CertificadoGerado[];
  loading: boolean;
  error: string | null;
  itemsRequestId: string | null;
  certificadoAcessos: CertificadoAcessosArbitral | null;
  certificadoAcessosLoading: boolean;
  certificadoAcessosError: string | null;
  certificadoAcessosRequestId: string | null;
  /**
   * Cobre a operação INTEIRA do botão "Baixar PDF" — pedido, espera do pdf-worker e busca dos
   * bytes. O erro não mora aqui de propósito: a tela não tem lugar para exibi-lo (o design não
   * previu), então o canal visível é o toast disparado por quem despachou, e um campo que
   * ninguém renderiza seria estado órfão.
   */
  certificadoAcessosPdfBaixando: boolean;
}

const initialState: CertificadosArbitraisState = {
  items: [],
  loading: false,
  error: null,
  itemsRequestId: null,
  certificadoAcessos: null,
  certificadoAcessosLoading: false,
  certificadoAcessosError: null,
  certificadoAcessosRequestId: null,
  certificadoAcessosPdfBaixando: false,
};

const certificadosArbitraisSlice = createSlice({
  name: 'certificadosArbitrais',
  initialState,
  reducers: {
    clearCertificadoAcessos: (state) => {
      state.certificadoAcessos = null;
      state.certificadoAcessosLoading = false;
      state.certificadoAcessosError = null;
      state.certificadoAcessosRequestId = null;
      state.certificadoAcessosPdfBaixando = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCertificados.pending, (state, action) => {
        state.itemsRequestId = action.meta.requestId;
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCertificados.fulfilled, (state, action) => {
        if (state.itemsRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCertificados.rejected, (state, action) => {
        if (state.itemsRequestId !== action.meta.requestId) return;
        state.loading = false;
        state.error = action.payload || 'Erro ao carregar certificados';
      })
      .addCase(fetchCertificadoAcessos.pending, (state, action) => {
        state.certificadoAcessosRequestId = action.meta.requestId;
        state.certificadoAcessosLoading = true;
        state.certificadoAcessosError = null;
      })
      .addCase(fetchCertificadoAcessos.fulfilled, (state, action) => {
        if (state.certificadoAcessosRequestId !== action.meta.requestId) return;
        state.certificadoAcessosLoading = false;
        state.certificadoAcessos = action.payload;
      })
      .addCase(fetchCertificadoAcessos.rejected, (state, action) => {
        if (state.certificadoAcessosRequestId !== action.meta.requestId) return;
        state.certificadoAcessosLoading = false;
        state.certificadoAcessosError =
          action.payload || 'Erro ao carregar o Certificado de Acessos';
      })
      .addCase(baixarPdfCertificadoAcessos.pending, (state) => {
        state.certificadoAcessosPdfBaixando = true;
      })
      .addCase(baixarPdfCertificadoAcessos.fulfilled, (state) => {
        state.certificadoAcessosPdfBaixando = false;
      })
      .addCase(baixarPdfCertificadoAcessos.rejected, (state) => {
        state.certificadoAcessosPdfBaixando = false;
      });
  },
});

export const { clearCertificadoAcessos } = certificadosArbitraisSlice.actions;
export const certificadosArbitraisReducer = certificadosArbitraisSlice.reducer;
